<template lang="pug">
#AddressSearch
    .only-screen
        label(for="address-search-field") Enter your address to get started:
        .relative
            vue-autosuggest(
                class="autosuggest"
                ref="addressSearchField"
                :suggestions="addressSuggestions"
                :input-props="{ class:'autosuggest__input', id: 'address-search-field', 'aria-label': 'Start typing your address and then use the arrow buttons to move through the list of matches.',onInputChange:updateItems }"

                :get-suggestion-value="getSuggestionValue"
                @selected="onSelected"
                :sectionConfigs="{ 'default': { limit: 30, } }"
            )
                template(slot-scope="{suggestion}")
                    .item {{ suggestion.item.text }}
            button.autocomplete-icon.autocomplete-icon-close(
                aria-label="Clear"
                v-if="addressQuery"
                @click="clearSuggestions"
            )
    .only-print(v-if="addressQuery")
        h3 Address
        p.address-text {{ addressQuery }}
</template>

<script>
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { getBoundariesForPoint } from '@/boundaryLookup';
import mapboxgl from 'mapbox-gl';
import distance from '@turf/distance';

import { VueAutosuggest } from 'vue-autosuggest';
import { EventBus } from './EventBus';
import { abbreviateStreet, expandAbbreviation } from './abbreviateStreet';

function titleCase(str) {
    str = str || '';
    return str.toLowerCase().replace(/(\b|\d)\S/g, (t) => t.toUpperCase());
}
function chooseGeocoder() {
    if ((window.location + '').match(/vicmap/)) {
        return 'vicmap';
    }
    if ((window.location + '').match(/mapshare/)) {
        return 'mapshare';
    }
    if ((window.location + '').match(/esri/)) {
        return 'esri';
    }
    return 'mapshare';
}
window.cg = chooseGeocoder;
export default {
    name: 'AddressSearch',
    props: ['id'],
    components: {
        VueAutosuggest,
    },
    data: () => ({
        addressQuery: null,
        addressSuggestions: [], // [{ data: [{ text: '1 Main St, Melbourne, VIC', coordinates: [ 144.95, -37.85 ], ...}]
        addressPoint: undefined,
        lastQuery: '',
        geocoder: chooseGeocoder(),
    }),
    created() {
        window.app.AddressSearch = this;
        EventBus.$on(
            'boundarySetChanged',
            (boundarySet, BoundarySelector, source) => {
                if (source === 'school-select') {
                    // if we got here because user specifically looked up the school in search or clickedon it, we don't want to potentially shift to a different school
                    return;
                }
                this.repeatFindBoundary();
            }
        );
    },
    mounted() {
        setTimeout(() => {
            // attempt to fix IE bug #154
            this.addressSuggestions = [];
            this.addressQuery = '';
            this.clearSuggestions(true);
        }, 100);
    },
    methods: {
        // vue-autosuggest
        async updateItemsEsri(query = '') {
            const results = await window
                .fetch(
                    'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/suggest?' +
                        new URLSearchParams({
                            f: 'json',
                            text: query,
                            searchExtent: [
                                140.91, -39.3, 150.1, -33.9,
                            ].toString(),
                            category: 'Address',
                        })
                )
                .then((response) => response.json());
            this.addressSuggestions = [
                {
                    data: results.suggestions.filter((s) =>
                        s.text.match(/, Victoria,/)
                    ),
                },
            ];
        },
        async updateItemsVicmap(q) {
            function expandFinalAbbreviation(query) {
                const m = query.match(/^(.+\s+)([A-Za-z][A-Za-z]+)$/);
                if (m) {
                    const ret = m[1] + expandAbbreviation(m[2]);
                    console.log(`Expanded ${query} to ${ret}`);
                    return ret;
                }
                return query;
            }
            const query = expandFinalAbbreviation(q);
            if (!query.startsWith(this.lastQuery)) {
                this.addressSuggestions = [];
            }
            this.lastQuery = query;

            const results = await window
                .fetch(
                    'https://wovg-shared.gateway.prod.api.vic.gov.au/delwp/address/v1/suggestions?' +
                        new URLSearchParams({ query }),
                    {
                        headers: {
                            apikey: '81947559-cea9-4603-8ff4-c47a9698a250',
                            Accept: 'application/json, text/plain, */*',
                        },
                    }
                )
                .then((response) => response.json());

            const suggestions = results.suggestions.filter(
                (s, i) =>
                    results.suggestions.findIndex(
                        (ss) => ss.label === s.label
                    ) === i
            );
            this.addressSuggestions = [
                {
                    data: suggestions.map((s) => ({
                        text: [
                            s.street_number,
                            titleCase(s.street_name),
                            titleCase(abbreviateStreet(s.street_type)) + ',',
                            titleCase(s.suburb),
                            s.postcode,
                        ]
                            .join(' ')
                            .trim(),
                        coordinates: [s.longitude, s.latitude],
                    })),
                },
            ];
        },
        async updateItemsMapshare(q) {
            //'https://corp-geo.mapshare.vic.gov.au/arcgis/rest/services/Geocoder/VMAddressEZIAdd/GeocodeServer/suggest?f=json&text=43%20fish'
            const results = await window
                .fetch(
                    'https://corp-geo.mapshare.vic.gov.au/arcgis/rest/services/Geocoder/VMAddressEZIAdd/GeocodeServer/suggest?' +
                        new URLSearchParams({
                            text: q,
                            f: 'json',
                            // category: 'Address',
                        })
                )
                .then((response) => response.json());

            // console.log(results);
            this.addressSuggestions = [
                {
                    data: results.suggestions.map((s) => ({
                        ...s,
                        text: titleCase(s.text),
                    })),
                },
            ];
        },
        async updateItems(query = '') {
            if (query.length <= 2) {
                return;
            }
            window.track('AddressSearch', 'suggest');
            if (this.geocoder === 'vicmap') {
                await this.updateItemsVicmap(query);
            } else if (this.geocoder === 'mapshare') {
                await this.updateItemsMapshare(query);
            } else {
                await this.updateItemsEsri(query);
            }
        },
        async onSelectedEsri(query) {
            const {
                candidates: [{ location }],
            } = await window
                .fetch(
                    'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates?' +
                        new URLSearchParams({
                            f: 'json',
                            SingleLine: query.item.text,
                            magicKey: query.item.magicKey,
                        })
                )
                .then((response) => response.json());
            this.setSelectedPoint(query.item.text, [location.x, location.y]);
        },
        async onSelectedVicmap(query) {
            this.setSelectedPoint(query.item.text, query.item.coordinates);
        },
        async onSelectedMapshare(query) {
            const locations = await window
                .fetch(
                    'https://corp-geo.mapshare.vic.gov.au/arcgis/rest/services/Geocoder/VMAddressEZIAdd/GeocodeServer/findAddressCandidates?' +
                        new URLSearchParams({
                            f: 'json',
                            SingleLine: query.item.text,
                            magicKey: query.item.magicKey,
                            spatialReference: { wkid: 4326 },
                        })
                )
                .then((response) => response.json());
            // console.log(locations);
            const {
                candidates: [{ location }],
            } = locations;
            this.setSelectedPoint(query.item.text, [location.x, location.y]);
        },
        async onSelected(query) {
            if (!query) {
                try {
                    // user pressed enter without selecting a value. let's use the first.
                    query = { item: this.addressSuggestions[0].data[0] };
                    this.$refs.addressSearchField.searchInput = query.item.text;
                } catch (e) {
                    window.track('AddressSearch', 'choose-null-suggestion');
                    return; // user presses enter before there are values
                }
            }
            window.track('AddressSearch', 'lookup-address');
            EventBus.$emit('address-select');
            if (this.geocoder === 'vicmap') {
                await this.onSelectedVicmap(query);
            } else if (this.geocoder === 'mapshare') {
                await this.onSelectedMapshare(query);
            } else {
                await this.onSelectedEsri(query);
            }
        },
        setSelectedPoint(queryText, coordinates) {
            const point = { type: 'Point', coordinates };
            this.showGeocodeResult(point, queryText);
            this.addressQuery = queryText;
            findRelatedBoundary(coordinates);
            window.track('AddressSearch', 'result');
            this._lastLocation = point;
        },

        getSuggestionValue(suggestion) {
            return suggestion.item.text;
        },
        clearSuggestions(self) {
            this.$refs.addressSearchField.searchInput = '';
            this.clearGeocodeResult();
            this._lastLocation = null;
            window.app.Results.zonedEntityCode = null;
            if (!self) {
                window.track('AddressSearch', 'clear');
            }
            setTimeout(() => {
                this.addressSuggestions = [];
                this.addressQuery = '';
            }, 1);
            setTimeout(() => {
                this.$refs.addressSearchField.$el.children[0].focus();
            }, 10);
            if (!self) {
                EventBus.$emit('boundaries-results', {
                    zonedSchools: [],
                    stage: window.app.BoundarySelector.stage,
                });
                EventBus.$emit('school-select');
            }
        },

        clearGeocodeResult() {
            if (window.map.getSource('geocode-result')) {
                window.map.U.setData('geocode-result', {
                    type: 'FeatureCollection',
                    features: [],
                });
            }
            if (this.marker) {
                this.marker.remove();
            }
            this.addressPoint = undefined;
            window.app.NearestSchools.schools = null;
        },
        showGeocodeResult(location, label) {
            map.U.setData('geocode-result', location);
            this.addressPoint = location.coordinates;

            const el = document.createElement('div');
            el.innerHTML = label;
            el.className = 'address-label';
            if (this.marker) {
                this.marker.remove();
            }
            this.marker = new mapboxgl.Marker({
                element: el,
            })
                .setLngLat(location.coordinates)
                .addTo(map);
            map.flyTo(location);
        },
        init(map) {},
        repeatFindBoundary() {
            if (this._lastLocation) {
                findRelatedBoundary(this._lastLocation.coordinates);
            } else {
                EventBus.$emit('school-select');
            }
        },
        click() {},
        keydown(e) {
            // Geocoder control swallows tab keys https://github.com/mapbox/mapbox-gl-geocoder/issues/162
            if (e.shiftKey && e.keyCode == 9) {
                document.getElementById('copyright-link').focus();
            } else if (e.keyCode == 9) {
                document.getElementById('yeargroup-primary').focus();
            }
        },
    },
};

function findNearestSchools(lngLat) {
    const sortFunc = (a, b) => distance(lngLat, a) - distance(lngLat, b);
    const closest = window.app.activeSchools().sort(sortFunc).slice(0, 5);
    window.app.Map.fitPoints(
        lngLat,
        ...closest.map((s) => s.geometry.coordinates)
    );
    return closest;
}

function dupe(obj) {
    return JSON.parse(JSON.stringify(obj));
}

function schoolForBoundary(boundary) {
    if (boundary) {
        return dupe(
            window.app
                .currentSchools()
                .features.find(
                    (s) =>
                        String(s.properties.Entity_Code) ===
                        String(boundary.Entity_Code)
                )
        );
    }
}

function isStandaloneJrSec(school) {}

function filterUniqueBoundaries(boundaries) {
    return boundaries.filter(
        (boundary, i, self) =>
            self.findIndex(
                (b) =>
                    !boundary || (b && b.Entity_Code === boundary.Entity_Code)
            ) === i
    );
}

async function findRelatedBoundary(lngLat) {
    const bs = window.app.BoundarySelector;
    if (!bs.year || !bs.stage) {
        return;
    }
    window.app.NearestSchools.schools = findNearestSchools(lngLat);
    if (bs.year === 'specialist') {
        EventBus.$emit('boundaries-results', {
            zonedSchools: [],
            stage: bs.stage,
        });
        // window.app.OtherZonedSchools.schoolsBoundaries = [];
        EventBus.$emit('school-select');
        return;
    }
    let years = [bs.year];
    if (bs.year === 'primary') {
        // none
    } else if (bs.year.match(/year(7|8|9)/)) {
        years.push('juniorsec');
    } else if (bs.year.match(/year(10|11|12)/)) {
        years.push('seniorsec');
    } else if (bs.year === 'singlesex') {
        years.push('year7');
    } else if (bs.year === 'juniorsec') {
        years.push('year7');
    } else if (bs.year === 'seniorsec') {
        years.push('year12');
    }
    if (bs.year.match(/year(7|8|9|10|11|12)/)) {
        years.push('singlesex');
    }
    const boundaries = await getBoundariesForPoint(bs.stage, years, lngLat);
    // See #187
    const sortFunc = (a, b) => {
        if (a.properties[`juniorsec_${bs.stage}`]) {
            return -1;
        } else if (b.properties[`juniorsec_${bs.stage}`]) {
            return 1;
        } else {
            return 0;
        }
    };
    const school = boundaries[0] && schoolForBoundary(boundaries[0]);
    const schools = boundaries
        .map((b, i) => {
            const s = schoolForBoundary(b);
            if (s) {
                const p = s.properties;
                p.queryYear = years[i];
                p.queryStage = bs.stage;
                p.queryMatch = p[bs.boundarySet.replace(/-/, '_')]
                    ? 'exact'
                    : 'compatible';
                p.isStandaloneJuniorsec =
                    !!p[`juniorsec_${bs.stage}`] && !p[`year7_${bs.stage}`];
                p.isStandaloneSeniorsec =
                    !!p[`seniorsec_${bs.stage}`] && !p[`year12_${bs.stage}`];
                p.isStandalone =
                    p.isStandaloneJuniorsec ||
                    p.isStandaloneSeniorsec ||
                    !!p[`singlesex_${bs.stage}`];
            }
            return s;
        })
        .filter(Boolean)
        .sort(sortFunc);

    EventBus.$emit('boundaries-results', {
        zonedSchools: schools,
        stage: bs.stage,
    });
    window.app.OtherZonedSchools.schoolsBoundaries = boundaries
        .map((b, i) => ({
            boundaryYear: years[i],
            school: schoolForBoundary(b),
        }))
        .filter((sb) => sb.school && sb.school !== school);
    if (!boundaries[0]) {
        map.flyTo({ center: lngLat }); // might as well go there?
        console.error('No boundary found for ', lngLat);
        window.track('AddressSearch', 'findrelated', 'noboundary');
        EventBus.$emit('school-select');
        return;
    }
    if (!school) {
        console.error('No school found for ', lngLat);
        window.track('AddressSearch', 'findrelated', 'noschool');
        EventBus.$emit('school-select');
        return;
    }
    EventBus.$emit('school-select', school, false, { isZoned: true, schools });

    window.app.Map.fitPoints(school.geometry.coordinates, lngLat);
}

import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
</script>

<style scoped>
#AddressSearch {
    margin: 2em 0 0;
}

.relative {
    position: relative;
}

label {
    color: #af272f;
    display: block;
    margin-bottom: 0.5em;
}

input {
    margin: 0;
}

button.autocomplete-icon-close {
    /* height: 35px; */
    margin: 0;
    padding: 10px 10px;
    border-radius: 3px;
    border: 1px solid grey;
    top: 10px;
    right: 5px;
}

/* .autosuggest {
    cursor: 'pointer';
} */
</style>
<style>
.mapboxgl-ctrl-geocoder {
    width: 100%;
    max-width: 100%;
    box-shadow: none;
}

.mapboxgl-ctrl-geocoder input[type='text'] {
    color: unset !important;
    border: 1px solid grey;
    border-radius: 3px;
    /* To override space left for search icon */
    padding: 10px;
    font-size: 16px;
}

.mapboxgl-ctrl-geocoder input[type='text']:focus {
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
}

.mapboxgl-ctrl-geocoder input[type='text']::placeholder {
    color: transparent;
}

.mapboxgl-ctrl-geocoder ul > li > a {
    font-family: 'VIC', 'Avenir', Helvetica, Arial, sans-serif;
}

span.geocoder-icon-search {
    display: none;
}

.address-label {
    font-family: 'VIC', 'Avenir', Helvetica, Arial, sans-serif;
    text-align: center;
    background: white;
    max-width: 150px;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.25);
    border-radius: 3px;
    padding: 0.5em;
    margin-top: -80px;
}
</style>
